import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import { DonationCta } from './components/DonationCta';
import { PrivacyBand } from './components/PrivacyBand';
import { ResultsSection } from './components/ResultsSection';
import { SearchSection } from './components/SearchSection';
import { ThemeMenu } from './components/ThemeMenu';
import { advancedDefaults } from './searchConfig';
import {
  getInitialAdvancedFilters,
  getInitialCategory,
  getInitialColorMode,
  getInitialPage,
  getInitialPalette,
  getInitialQuery,
} from './searchUtils';
import { ResultMetadata, SearxngResponse } from './types';

function App() {
  const [query, setQuery] = useState(getInitialQuery);
  const [submittedQuery, setSubmittedQuery] = useState(getInitialQuery);
  const [category, setCategory] = useState(getInitialCategory);
  const [advancedFilters, setAdvancedFilters] = useState(getInitialAdvancedFilters);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [showPaletteControls] = useState(() =>
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('theme')
  );
  const [palette, setPalette] = useState(getInitialPalette);
  const [colorMode, setColorMode] = useState(getInitialColorMode);
  const [data, setData] = useState<SearxngResponse | null>(null);
  const [page, setPage] = useState(getInitialPage);
  const [metadataByUrl, setMetadataByUrl] = useState<Record<string, ResultMetadata>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [shareStatus, setShareStatus] = useState('');
  const abortController = useRef<AbortController | null>(null);
  const didMountPageReset = useRef(false);

  const hasSearched = submittedQuery.trim().length > 0;
  const results = useMemo(() => data?.results ?? [], [data?.results]);
  const isImageSearch = category === 'images';
  const isVideoSearch = category === 'videos';
  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined') {
      return '';
    }

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('q', submittedQuery.trim());
    nextUrl.searchParams.set('category', category);
    nextUrl.searchParams.set('language', advancedFilters.language);
    nextUrl.searchParams.set('safesearch', advancedFilters.safeSearch);
    nextUrl.searchParams.set('page', String(page));

    if (advancedFilters.timeRange) {
      nextUrl.searchParams.set('time_range', advancedFilters.timeRange);
    } else {
      nextUrl.searchParams.delete('time_range');
    }

    return nextUrl.toString();
  }, [advancedFilters.language, advancedFilters.safeSearch, advancedFilters.timeRange, category, page, submittedQuery]);

  const resultCount = useMemo(() => {
    if (!hasSearched || isLoading) {
      return '';
    }

    if (!results.length) {
      return 'No results found';
    }

    return `${results.length} result${results.length === 1 ? '' : 's'}`;
  }, [hasSearched, isLoading, results.length]);

  useEffect(() => {
    const nextUrl = new URL(window.location.href);

    if (showPaletteControls) {
      nextUrl.searchParams.set('theme', palette);
      window.history.replaceState(null, '', nextUrl);
    }

    window.localStorage.setItem('gotconn-palette', palette);
  }, [palette, showPaletteControls]);

  useEffect(() => {
    window.localStorage.setItem('gotconn-color-mode', colorMode);
  }, [colorMode]);

  useEffect(() => {
    const trimmedQuery = submittedQuery.trim();

    if (!trimmedQuery) {
      setData(null);
      setError('');
      setIsLoading(false);
      return;
    }

    abortController.current?.abort();

    const controller = new AbortController();
    abortController.current = controller;

    const searchParams = new URLSearchParams({
      q: trimmedQuery,
      format: 'json',
      language: advancedFilters.language,
      safesearch: advancedFilters.safeSearch,
      categories: category,
      pageno: String(page),
    });

    if (advancedFilters.timeRange) {
      searchParams.set('time_range', advancedFilters.timeRange);
    }

    setIsLoading(true);
    setError('');
    setMetadataByUrl({});

    fetch(`/api/search?${searchParams.toString()}`, {
      headers: {
        Accept: 'application/json',
        'X-GotConn-Search': '1',
      },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Search failed with status ${response.status}`);
        }

        const contentType = response.headers.get('content-type') || '';

        if (!contentType.includes('application/json')) {
          throw new Error(`Search returned ${contentType || 'an unknown content type'}`);
        }

        return response.json() as Promise<SearxngResponse>;
      })
      .then((payload) => setData(payload))
      .catch((searchError: Error) => {
        if (searchError.name === 'AbortError') {
          return;
        }

        setData(null);
        setError(`Search is unavailable right now. ${searchError.message}`);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('q', trimmedQuery);
    nextUrl.searchParams.set('category', category);
    nextUrl.searchParams.set('language', advancedFilters.language);
    nextUrl.searchParams.set('safesearch', advancedFilters.safeSearch);
    nextUrl.searchParams.set('page', String(page));

    if (advancedFilters.timeRange) {
      nextUrl.searchParams.set('time_range', advancedFilters.timeRange);
    } else {
      nextUrl.searchParams.delete('time_range');
    }

    window.history.replaceState(null, '', nextUrl);

    return () => controller.abort();
  }, [advancedFilters.language, advancedFilters.safeSearch, advancedFilters.timeRange, category, page, submittedQuery]);

  useEffect(() => {
    if (!didMountPageReset.current) {
      didMountPageReset.current = true;
      return;
    }

    setPage(1);
  }, [advancedFilters.language, advancedFilters.safeSearch, advancedFilters.timeRange, category, submittedQuery]);

  useEffect(() => {
    if (isImageSearch || isVideoSearch || !results.length) {
      return;
    }

    const controller = new AbortController();
    const urls = Array.from(new Set(results.map((result) => result.url).filter(Boolean))).slice(0, 8) as string[];

    Promise.allSettled(
      urls.map((url) =>
        fetch(`/metadata?url=${encodeURIComponent(url)}`, { signal: controller.signal })
          .then((response) => response.ok ? response.json() as Promise<ResultMetadata> : null)
      )
    ).then((metadataResults) => {
      if (controller.signal.aborted) {
        return;
      }

      const nextMetadata: Record<string, ResultMetadata> = {};

      metadataResults.forEach((metadataResult) => {
        if (metadataResult.status === 'fulfilled' && metadataResult.value?.url) {
          nextMetadata[metadataResult.value.url] = metadataResult.value;
        }
      });

      setMetadataByUrl(nextMetadata);
    });

    return () => controller.abort();
  }, [isImageSearch, isVideoSearch, results]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(1);
    setSubmittedQuery(query);
  };

  const clearSearch = () => {
    setQuery('');
    setSubmittedQuery('');
    setPage(1);
    setData(null);
    setMetadataByUrl({});
    setError('');
    const nextUrl = new URL(window.location.href);
    ['q', 'category', 'language', 'safesearch', 'page', 'time_range'].forEach((param) => nextUrl.searchParams.delete(param));
    window.history.replaceState(null, '', nextUrl);
  };

  const updateAdvancedFilter = (name: keyof typeof advancedDefaults, value: string) => {
    setAdvancedFilters((currentFilters) => ({
      ...currentFilters,
      [name]: value,
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters(advancedDefaults);
  };

  const submitRelatedSearch = (nextQuery: string) => {
    setQuery(nextQuery);
    setPage(1);
    setSubmittedQuery(nextQuery);
  };

  const shareSearch = async () => {
    const title = `GotConn search: ${submittedQuery}`;
    const text = `Search results for "${submittedQuery}"`;

    try {
      if (navigator.share) {
        await navigator.share({ title, text, url: shareUrl });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        setShareStatus('Link copied');
        window.setTimeout(() => setShareStatus(''), 2200);
      }
    } catch (shareError) {
      if (shareError instanceof DOMException && shareError.name === 'AbortError') {
        return;
      }

      setShareStatus('Unable to share');
      window.setTimeout(() => setShareStatus(''), 2200);
    }
  };

  return (
    <main
      className={`app-shell ${hasSearched ? 'has-results' : ''}`}
      data-palette={palette}
      data-color-mode={colorMode}
    >
      <ThemeMenu
        colorMode={colorMode}
        palette={palette}
        showPaletteControls={showPaletteControls}
        showThemeMenu={showThemeMenu}
        onColorModeChange={setColorMode}
        onPaletteChange={setPalette}
        onToggleThemeMenu={() => setShowThemeMenu((isOpen) => !isOpen)}
      />

      <SearchSection
        advancedFilters={advancedFilters}
        category={category}
        query={query}
        showDonationCta={hasSearched}
        showAdvanced={showAdvanced}
        onCategoryChange={(nextCategory) => {
          setPage(1);
          setCategory(nextCategory);
        }}
        onClearSearch={clearSearch}
        onQueryChange={setQuery}
        onResetAdvancedFilters={resetAdvancedFilters}
        onSubmit={submitSearch}
        onToggleAdvanced={() => setShowAdvanced((isOpen) => !isOpen)}
        onUpdateAdvancedFilter={updateAdvancedFilter}
      />

      {!hasSearched && (
        <>
          <PrivacyBand />
          <section className="landing-donation" aria-label="Support GOT Connections">
            <DonationCta />
          </section>
        </>
      )}

      {hasSearched && (
        <ResultsSection
          data={data}
          error={error}
          isImageSearch={isImageSearch}
          isLoading={isLoading}
          isVideoSearch={isVideoSearch}
          metadataByUrl={metadataByUrl}
          page={page}
          resultCount={resultCount}
          results={results}
          shareStatus={shareStatus}
          submittedQuery={submittedQuery}
          onCorrectionSearch={submitRelatedSearch}
          onNextPage={() => setPage((currentPage) => currentPage + 1)}
          onPreviousPage={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
          onShareSearch={shareSearch}
          onSuggestionSearch={submitRelatedSearch}
        />
      )}
    </main>
  );
}

export default App;
